import { getGraphqlClient } from "@/services/graphqlEndPointCommonSetting/gqlClient";

const MAX_ATTEMPTS = 5;
const BACKOFF_MS = [1000, 2000, 3000, 5000];

/**
 * Placing an order converts the quote and deactivates it, so these codes mean
 * the order already exists rather than that anything failed. Retrying past
 * them can only produce noise.
 */
const ALREADY_PLACED_CODES = ["CART_NOT_ACTIVE", "CART_NOT_FOUND"];

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Probes the quote directly instead of going through wrapGqlRequest: this query
 * is *expected* to fail once the quote has been converted, and the shared error
 * handler would file an incident report for every probe.
 *
 * Returns false when it cannot tell. That is the conservative answer — it stops
 * us firing a second placeOrder alongside one that may still be running, at the
 * cost of handing off to the background job slightly earlier than needed.
 *
 * @param {string} cartId - Masked quote id; ignored when useCustomerCart is set
 * @param {boolean} useCustomerCart - Logged-in carts resolve via customerCart
 * @returns {Promise<boolean>}
 */
async function isCartStillActive(cartId, useCustomerCart) {
  const query = useCustomerCart
    ? `query { customerCart { id } }`
    : `query ($cartId: String!) { cart(cart_id: $cartId) { id } }`;

  try {
    const client = getGraphqlClient({ passAuthHeader: true });
    const response = await client
      .query(query, useCustomerCart ? {} : { cartId })
      .toPromise();

    if (response.error) return false;
    return Boolean(response.data?.cart?.id || response.data?.customerCart?.id);
  } catch (error) {
    return false;
  }
}

/**
 * Shared failure policy for cart mutations attempted after payment capture.
 *
 * @returns {Promise<"placed"|"stop"|"retry">}
 */
async function classifyFailure(error, attempt, cartId, useCustomerCart) {
  if (ALREADY_PLACED_CODES.includes(error?.code)) return "placed";

  // The session expired: the shared handler has already redirected and filed an
  // incident, so retrying just multiplies both.
  if (error?.isAuthError) return "stop";

  if (attempt === MAX_ATTEMPTS) return "stop";

  // No code means the mutation never reported back (abort or timeout) and may
  // still be running on the server. Confirm the quote has not already been
  // converted before firing a second, concurrent attempt.
  if (!error?.code && !(await isCartStillActive(cartId, useCustomerCart))) {
    return "placed";
  }

  return "retry";
}

/**
 * Retries order placement — never payment. By the time this runs Stripe has
 * already captured, so the only thing left to get right is the Commerce call.
 *
 * Retrying is safe because placeOrder converts the quote: an attempt that
 * succeeded without us seeing the response comes back CART_NOT_ACTIVE next
 * time instead of creating a second order.
 *
 * @param {object} options
 * @param {Function} options.placeOrder - Runs one placeOrder mutation
 * @param {string} options.cartId - Masked quote id, for the timeout probe
 * @param {boolean} [options.useCustomerCart] - Set for logged-in customers
 * @returns {Promise<{orderV2?: object, alreadyPlaced?: boolean, code?: string, message?: string}>}
 */
export async function placeOrderWithRetry({
  placeOrder,
  cartId,
  useCustomerCart = false,
}) {
  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
    try {
      const response = await placeOrder();
      if (response?.orderV2) return { orderV2: response.orderV2 };
      return { message: "Unknown error occurred" };
    } catch (error) {
      const verdict = await classifyFailure(
        error,
        attempt,
        cartId,
        useCustomerCart
      );
      if (verdict === "placed") {
        return { alreadyPlaced: true, code: error?.code ?? "CART_NOT_ACTIVE" };
      }
      if (verdict === "stop") throw error;

      await sleep(BACKOFF_MS[attempt - 1]);
    }
  }
}

/**
 * Retries setting the payment method on the quote. This runs *after* the card
 * has been charged, and until it succeeds placeOrder is never even attempted —
 * so a single transient failure here used to mean a captured payment with zero
 * placement attempts.
 *
 * Safe to repeat: it writes a field on the quote, so applying the same method
 * twice is a no-op. Kept separate from the placeOrder retry rather than folded
 * into it, because re-sending it before every placement attempt would be
 * wasted work — it persists once set.
 *
 * @param {object} options
 * @param {Function} options.setPaymentMethod - Runs one setPaymentMethodOnCart
 * @param {Function} options.isSuccess - Reads success out of the response
 * @param {string} options.cartId - Masked quote id, for the timeout probe
 * @param {boolean} [options.useCustomerCart] - Set for logged-in customers
 * @returns {Promise<{response?: object, alreadyPlaced?: boolean, code?: string, failed?: boolean}>}
 */
export async function setPaymentMethodWithRetry({
  setPaymentMethod,
  isSuccess,
  cartId,
  useCustomerCart = false,
}) {
  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
    try {
      const response = await setPaymentMethod();
      if (isSuccess(response)) return { response };

      // Returned without throwing, but without applying the method either.
      if (attempt === MAX_ATTEMPTS) return { failed: true };
    } catch (error) {
      const verdict = await classifyFailure(
        error,
        attempt,
        cartId,
        useCustomerCart
      );
      // The quote is already an order, so there is nothing left to set.
      if (verdict === "placed") {
        return { alreadyPlaced: true, code: error?.code ?? "CART_NOT_ACTIVE" };
      }
      if (verdict === "stop") throw error;
    }

    await sleep(BACKOFF_MS[attempt - 1]);
  }
}
